import { FC } from 'react';
import { useTranslation } from 'react-i18next';

import { Box, Typography } from '@mui/material';
import clsx from 'clsx';
import { useBusinessToolStore } from 'store/useBusinessToolStore';
import { getBusinessPlan, getSetBusinessPlan } from 'store/useBusinessToolStore/selectors';

import { ReactComponent as CheckCircleSmall } from 'assets/icons/CheckCircleSmall.svg';
import { ReactComponent as EmptyDocumentsIcon } from 'assets/icons/EmptyDocumentsIcon.svg';
import templateStyle from 'assets/images/templateStyle.png';

import { getValidatedMetadata } from 'helpers/metadataParser';

import { DOCUMENT_STYLE_OPTIONS } from './constant';
import { IDocumentStyleType } from './types';
import { UPDATE_HANDLER_ARGUMENTS } from '../PreviewModeComponent/hooks/constants';
import { IDocumentStyleUpdateParams } from '../PreviewModeComponent/hooks/types';
import { useDebounceUpdateMetadataHandler } from '../PreviewModeComponent/hooks/useDebounceUpdateMetadataHandler';

import styles from './DocumentStyleSelector.module.scss';

export const DocumentStyleSelector: FC = () => {
  const { t } = useTranslation();

  const businessPlan = useBusinessToolStore(getBusinessPlan);
  const setBusinessPlan = useBusinessToolStore(getSetBusinessPlan);

  const selectedStyle = businessPlan.metadata.documentStyleType;

  const handleDocumentStyleSelect = async (style: IDocumentStyleType) => {
    if (selectedStyle === style) return;
    setBusinessPlan({ ...businessPlan, metadata: { ...getValidatedMetadata(businessPlan.metadata), documentStyleType: style } });

    debouncedUpdateHandler(style, businessPlan, selectedStyle);
  };

  const debouncedUpdateHandler = useDebounceUpdateMetadataHandler<IDocumentStyleUpdateParams>({
    metaDataKey: UPDATE_HANDLER_ARGUMENTS.DOCUMENT_STYLE.METADATA_KEY,
    errorMessage: UPDATE_HANDLER_ARGUMENTS.DOCUMENT_STYLE.ERROR_MESSAGE,
  });

  return (
    <Box className={styles.selectStylesContainer}>
      <Typography variant="h5" className={styles.selectStyleText}>
        {t('selectStyle')}
      </Typography>
      <Box className={styles.cardContainer}>
        {DOCUMENT_STYLE_OPTIONS.map(({ style }) => {
          const isSelected = style === selectedStyle;
          return (
            <Box
              key={style}
              onClick={() => handleDocumentStyleSelect(style)}
              className={clsx(styles.themeSelectStyle, styles.cardWrapper, { [styles.selected]: isSelected })}
              sx={{ backgroundImage: `url(${templateStyle})` }}
            >
              {isSelected && (
                <Box className={styles.checkCircle}>
                  <CheckCircleSmall />
                </Box>
              )}
            </Box>
          );
        })}
        <Box className={clsx(styles.themeSelectStyle, styles.comingSoonCard)}>
          <Box sx={{ width: 'fit-content', margin: '0 auto' }}>
            <EmptyDocumentsIcon />
          </Box>
          <Typography className={styles.moreComingSoonText} variant="body1">
            {t('moreComingSoon')}
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};
